import React from 'react';

const statCards = [
  { key: 'farms', label: '农场数' },
  { key: 'plots', label: '地块数' },
  { key: 'devices', label: '在线设备' },
  { key: 'warnings', label: '预警数' },
  { key: 'activeJobs', label: '进行中作业' },
  { key: 'crops', label: '在种作物' },
];

/** 去掉坐标等噪声，横幅只留可读地名 */
function shortenPlaceLabel(raw) {
  if (!raw) return '';
  let s = String(raw)
    .replace(/\s*\(\s*-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?\s*\)/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (s.length > 22) {
    const parts = s.split(/[·\s]+/).filter(Boolean);
    if (parts.length >= 2) {
      s = parts.slice(-2).join(' · ');
    } else {
      s = `${s.slice(0, 20)}…`;
    }
  }
  return s;
}

function resolvePlaceText({ address, farmFallbackLabel }) {
  if (address?.city || address?.province) {
    const cityRaw = address.city && address.city !== '[]' ? address.city : address.province;
    const district =
      address.district && !String(cityRaw).includes(address.district) ? address.district : '';
    return shortenPlaceLabel(district ? `${cityRaw} · ${district}` : cityRaw);
  }
  if (farmFallbackLabel) return shortenPlaceLabel(farmFallbackLabel);
  return '';
}

const WelcomeBanner = ({
  userName,
  weather,
  currentDateStr,
  address,
  onViewWeatherTrend,
  onOpenWeatherPage,
  farmFallbackLabel = '',
  overviewStats,
  statsClickable = false,
  statsLoading = false,
  statsHint = '',
  onStatClick,
}) => {
  const displayName = userName || '农友';
  const placeText = resolvePlaceText({ address, farmFallbackLabel });

  const hasWeather = Boolean(
    weather && (weather.temp != null || weather.tempMax != null || weather.humidity != null)
  );

  const stats = overviewStats ?? {
    farms: 0,
    plots: 0,
    devices: 0,
    warnings: 0,
    activeJobs: 0,
    crops: 0,
  };

  const windText = [
    weather?.windDir,
    weather?.windScale != null && weather?.windScale !== '' ? `${weather.windScale}级` : null,
  ]
    .filter(Boolean)
    .join(' ');

  const openWeather = () => {
    if (typeof onOpenWeatherPage === 'function') onOpenWeatherPage();
    else onViewWeatherTrend?.();
  };

  const canOpenWeather =
    typeof onViewWeatherTrend === 'function' || typeof onOpenWeatherPage === 'function';

  return (
    <div className="relative rounded-xl overflow-hidden mb-8 bg-gradient-to-br from-green-800 via-green-700 to-emerald-700">
      <div className="relative z-10 px-5 py-5 sm:px-6 space-y-5">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
              你好，{displayName}
            </h1>
            <p className="mt-1 text-sm text-green-100">
              曲水稻香智慧农场
              {statsHint ? <span className="ml-2 text-green-200">{statsHint}</span> : null}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-green-50">
              {placeText ? (
                <span className="truncate max-w-[16rem] sm:max-w-xs" title={placeText}>
                  {placeText}
                </span>
              ) : null}
              {placeText ? (
                <span className="text-green-200/80" aria-hidden>
                  ·
                </span>
              ) : null}
              <span>{currentDateStr}</span>
            </div>
          </div>

          {hasWeather ? (
            <div className="flex flex-wrap items-end gap-x-4 gap-y-2 shrink-0">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-white tabular-nums leading-none">
                  {weather?.temp != null ? `${weather.temp}°C` : `${weather?.tempMax ?? '--'}°C`}
                </span>
                <span className="text-sm text-green-50">{weather?.text || '--'}</span>
              </div>
              <div className="text-sm text-green-100 flex flex-wrap gap-x-3">
                {windText ? <span>{windText}</span> : null}
                <span>
                  湿度{' '}
                  {weather?.humidity != null && weather?.humidity !== ''
                    ? `${weather.humidity}%`
                    : '--'}
                </span>
              </div>
              {canOpenWeather ? (
                <button
                  type="button"
                  onClick={openWeather}
                  className="text-sm font-semibold text-white underline underline-offset-4 decoration-green-200/80 hover:decoration-white"
                >
                  查看天气
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-3 lg:grid-cols-6 gap-px rounded-lg overflow-hidden bg-white/20">
          {statCards.map((card) => (
            <div
              key={card.key}
              role={statsClickable ? 'button' : undefined}
              tabIndex={statsClickable ? 0 : undefined}
              onClick={statsClickable ? () => onStatClick?.(card.key) : undefined}
              onKeyDown={
                statsClickable
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onStatClick?.(card.key);
                      }
                    }
                  : undefined
              }
              title={statsClickable ? '点击查看各租户明细' : undefined}
              className={`bg-green-900/35 px-3 py-3 text-center sm:text-left ${
                statsClickable
                  ? 'cursor-pointer hover:bg-green-900/55 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white/40'
                  : ''
              }`}
            >
              <div className="text-xl sm:text-2xl font-bold text-white tabular-nums leading-none">
                {statsLoading ? '—' : stats[card.key] || 0}
              </div>
              <div className="mt-1.5 text-xs text-green-100">{card.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WelcomeBanner;
